// src/ProtectedClientRoute.js
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';

const ProtectedClientRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation(); // Add this to access the current location

  useEffect(() => {
    // Check if user is logged in
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    
    // User must exist and be a client
    if (user && (user.role === 'client' || user.role === 'admin')) {
      setIsAuthenticated(true);
      setLoading(false);
    } else {
      // For testing/development - let's attempt to create a temporary guest session
      console.log('No authenticated user found - creating guest session');
      createGuestSession();
    }
  }, []);

  // Function to create a guest session for testing
  const createGuestSession = async () => {
    try {
      // For development only - register a temporary guest user
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: `guest-${Date.now()}@example.com`,
          password: 'guestpassword123',
          firstName: 'Guest',
          lastName: 'User',
          role: 'client'
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Created guest session', data);
        
        // Store user info in localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        setIsAuthenticated(true);
      } else {
        console.error('Failed to create guest session');
        setError('Authentication required. Please log in to view this page.');
      }
    } catch (err) {
      console.error('Error creating guest session:', err);
      setError('Authentication required. Please log in to view this page.');
    } finally {
      setLoading(false);
    }
  };

  // Handle login redirect button click
  const handleLoginRedirect = () => {
    // Pass the current location to the login page so it can redirect back after successful login
    navigate('/login', { 
      state: { 
        from: location.pathname,
        message: 'Please log in to view your profile'
      } 
    });
  };

  if (loading) {
    // Show loading indicator while checking authentication
    return (
      <div className="min-h-screen bg-blue-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    // Show error message with login button
    return (
      <div className="min-h-screen bg-blue-50 flex justify-center items-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="flex items-center text-red-500 mb-4">
            <AlertCircle className="mr-2" />
            <h2 className="text-lg font-semibold">Authentication Required</h2>
          </div>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={handleLoginRedirect}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login if not authenticated, passing the current location
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  // Render children if authenticated
  return children;
};

export default ProtectedClientRoute;